import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { currencyFormat } from '../../../../../../../Apis/functions';
import NotInformation from '../../../../../../../components/NotInformation';
import CustomChip from '../../../../../../../components/CustomChip';
import CustomTooltip from "../../../../../../../components/CustomTooltip";
import OvertimeBreakdownModal from "../Modal/OvertimeBreakdownModal";
import {PLACEMENT_MODEL} from "../../../../../../../Helpers/PositionModel";
import BaseSalaryModal from "../Modal/BaseSalaryModal";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Currency display with a $0 fallback for null/undefined values.
export const formatCurrency = (value) =>
    value != null ? `$${currencyFormat(value)}` : `$${currencyFormat(0)}`;

// Widest formatted amount in a list (including the total), used to reserve
// the value column width so amounts and totals line up.
export const getWidestAmount = (lines = [], key, total) =>
    [...lines.map((line) => line[key]), total].reduce((widest, value) => {
        const formatted = currencyFormat(value);
        return formatted.length > widest.length ? formatted : widest;
    }, '');

// Currency cell with a hidden sizer so every amount reserves the same width.
// When a breakdown is available the amount becomes clickable to open it in a modal.
export const PayrollAmount = ({ value, widest, bold = false, leadingRow, breakdown, onBreakdownClick }) => {
    const clickable = !!breakdown;

    return (
        <div className={`col-auto fs-14px ibl-font-w-${bold ? '700' : '600'} payroll-amount`}>
            <span>$</span>
            <span className='payroll-amount-value'>
                <span className='payroll-amount-sizer' aria-hidden='true'>{widest}</span>
                <span
                    className={`payroll-amount-num ${clickable ? ' cursor-pointer text-decoration-underline' : ''}`}
                    onClick={clickable ? onBreakdownClick : undefined}
                >
                    {currencyFormat(value)}
                </span>
            </span>
        </div>
    );
};

// A single name/legend + amount row with a bottom divider.
// `breakdown` is the line item; when present the amount opens its detail modal.
export const PayrollLineRow = ({ name, legend, breakdown, amount, widest, leadingRow }) => {
    const [showBreakdown, setShowBreakdown] = useState(false);

    return <div>
            <div className='row g-0 flex-nowrap align-items-center'>
                <div className='col me-2 minw-0'>
                    <CustomTooltip
                        text={name}
                        classes={`fs-14px font-color-general-700 text-ellipsis-1-row ${leadingRow ? 'ibl-font-w-600' : ''}`}
                        showIfTruncated
                        placement='top'
                    />

                    {legend &&
                        <CustomTooltip
                            text={legend}
                            classes='font-color-general-950 ibl-font-w-500 fs-12px text-ellipsis-1-row'
                            showIfTruncated
                            placement='top'
                        />
                    }
                </div>
                <PayrollAmount
                    value={amount}
                    widest={widest}
                    leadingRow={leadingRow}
                    breakdown={breakdown}
                    onBreakdownClick={() => setShowBreakdown(true)}
                />
            </div>
            <div className={`${leadingRow ? 'border-bottom-shades-300-2px' : 'border-bottom-shades-300'} my-1`}/>
            {breakdown && showBreakdown && (
                leadingRow
                    ? <BaseSalaryModal
                        show={showBreakdown}
                        item={breakdown}
                        onHide={() => setShowBreakdown(false)}
                    />
                    : <OvertimeBreakdownModal
                        show={showBreakdown}
                        item={breakdown}
                        onHide={() => setShowBreakdown(false)}
                    />
            )}
        </div>
};

// Renders a perceptions/deductions column: optional leading row, line items, total row.
const PayrollLinesColumn = ({
    title,
    tooltip,
    lines = [],
    getName,
    getLegend,
    getBreakdown,
    getAmount,
    widest,
    total,
    titleIcon,
    chipColors,
    leadingRow,
    className = 'col-sm-12 col-md-12 col-lg-6',
}) => {
    const { t } = useTranslation();

    return (
        <div className={`${className} minw-0`}>
            <div className={"d-flex align-items-center mb-2"}>
                <div className={'d-flex align-items-center mr-2'}>{titleIcon}</div>
                <p className='fs-16px ibl-font-w-600 font-color-general-950'>{title}</p>
                <div className={"d-flex justify-content-end ms-auto"}>
                    <CustomChip
                        background={chipColors.background}
                        fontColor={chipColors.fontColor}
                        borderColor={chipColors.borderColor}
                        legend={
                            tooltip ?
                                <CustomTooltip
                                    text={tooltip}
                                    classes={`fs-12px text-ellipsis-1-row`}
                                    showIfTruncated={false}
                                    placement={PLACEMENT_MODEL.TOP}
                                >
                                    {formatCurrency(total)}
                                </CustomTooltip>
                            :formatCurrency(total)
                        }
                        className={"flex-grow-0 minw-0"}
                        variant={'compact'}
                    />
                </div>
            </div>
            <div className='row g-0 flex-nowrap align-items-center mb-1'>
                <div className='col me-2 minw-0'>
                    <p className='fs-14px ibl-font-w-600 font-color-general-700 mb-0'>{t('')}</p>
                </div>
                <div className='col-auto'>
                    <p className='fs-14px ibl-font-w-600 font-color-general-700 mb-0 text-end'>{t('accumulated')}</p>
                </div>
            </div>
            <div className={`border-bottom-shades-300-2px my-1`}/>
            {leadingRow}
            {lines.length > 0 ? (
                lines.map((item, i) => (
                    <PayrollLineRow
                        key={i}
                        name={getName(item)}
                        legend={getLegend ? getLegend(item, i) : undefined}
                        breakdown={getBreakdown ? getBreakdown(item, i) : undefined}
                        amount={getAmount(item)}
                        widest={widest}
                        leadingRow={false}
                    />
                ))
            ) : (
                !leadingRow && <NotInformation text={t('no_results_found')} card={true} />
            )}
            {(lines.length > 0 || leadingRow) && (
                <div className='border-radius-8px py-2 mb-2'>
                    <div className='row g-0 flex-nowrap align-items-center'>
                        <div className='col me-2'>
                            <p className='fs-14px ibl-font-w-600 font-color-general-950 mb-0'>{t('total').toUpperCase()}</p>
                        </div>
                        <PayrollAmount value={total} widest={widest} bold leadingRow={leadingRow} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PayrollLinesColumn;
